import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, RedirectRequest } from '@azure/msal-browser';
import { environment } from 'environments/environment';
import { UserRolesCacheService } from './user-roles-cache.service';

/** Ruta de la pantalla de bloqueo para cuentas sin ningún rol. */
export const NO_ACCESS_ROUTE = '/no-access';

/**
 * Decide si la cuenta con la que se entró puede usar Sym70 **en absoluto**, y la saca si no.
 *
 * ⚠️ Por qué existe: un usuario autenticado en Entra pero SIN NINGÚN rol de la app entraba igual.
 * Los guards de módulo (`calidadAccessGuard`, `talentoHumanoAccessGuard`, …) solo cubren su propio
 * módulo, y el resto de la app no tenía gate: la persona llegaba al home, el layout disparaba sus
 * peticiones y el backend devolvía 403 en cascada
 * (`Roles del usuario: []. Política requerida: ReadPolicy`) — tres toasts de "Sin permisos" sobre
 * una pantalla vacía, sin forma de entender que el problema era la cuenta ni de cambiarla, porque
 * la app no expone logout fuera del layout.
 *
 * La fuente de verdad es el claim `roles` del token, no `UserRolesCacheService`:
 * - Son los roles de Sym70WebApp (el id token). OJO: la API valida con OTRA app, Sym70WebApi (el
 *   access token), así que el backend cita en su 403 los roles de esa. Cada rol tiene que estar
 *   asignado en las dos; Configuración → Usuarios y Roles las mantiene iguales.
 * - Es síncrona y sin red. El cache resuelve por Graph (`officeLocation` + listado de asignaciones)
 *   y puede tardar o fallar; usarlo acá significaría bloquear la primera navegación de TODA la app
 *   detrás de una llamada a Graph, y un fallo de Graph expulsaría a un usuario que sí tiene roles.
 *
 * No inyecta nada que dependa de `HttpClient` (ni `UserRolesCacheService`, que arrastra
 * `GraphService`): `AuthErrorInterceptor` la inyecta, y un `HttpClient` en esa cadena cierra un
 * ciclo de DI. El cache de roles se resuelve tarde, vía `Injector`.
 */
@Injectable({ providedIn: 'root' })
export class AppAccessService {
    private readonly msal = inject(MsalService);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);

    /** Evita re-navegar a /no-access con cada 403 de una tanda paralela. */
    private expelling = false;

    /** Cuenta activa; cae a la primera del cache porque `setActiveAccount` corre antes del bootstrap. */
    get account(): AccountInfo | null {
        try {
            const active = this.msal.instance.getActiveAccount();
            if (active) {
                return active;
            }
        } catch {
            // Mismo motivo que `accounts()`: leer el cache puede lanzar. Se cae al listado.
        }
        return this.accounts()[0] ?? null;
    }

    get isAuthenticated(): boolean {
        return this.accounts().length > 0;
    }

    /**
     * Cuentas del cache, sin poder tirar la navegación.
     *
     * `getAllAccounts()` NO es una lectura inocua: desde msal-browser v4 el cache de `localStorage`
     * va cifrado y solo se descifra después de `initialize()`, así que leerlo antes lanza
     * `uninitialized_public_client_application`. `main.ts` inicializa MSAL antes del bootstrap
     * justamente para que eso no pase (ver `initializeApp.ts`), pero este getter lo consume
     * `appAccessGuard` — que Angular corre en paralelo con `MsalGuard` — y una excepción síncrona
     * ahí no es un guard que devuelve `false`: es la navegación entera abortada. Ante la duda,
     * "no hay cuenta": `MsalGuard` lanza el login y no queda nadie con la pantalla en blanco.
     */
    private accounts(): AccountInfo[] {
        try {
            return this.msal.instance.getAllAccounts();
        } catch (err) {
            console.warn('No se pudo leer el cache de cuentas de MSAL', err);
            return [];
        }
    }

    /** Correo de la cuenta, para que la pantalla de bloqueo diga CON QUÉ cuenta se entró. */
    get accountEmail(): string {
        const account = this.account;
        return account?.username ?? (account?.idTokenClaims?.['preferred_username'] as string | undefined) ?? '';
    }

    get accountName(): string {
        return this.account?.name ?? '';
    }

    /** Roles de la app en el token de la cuenta actual. */
    tokenRoles(): string[] {
        const roles = this.account?.idTokenClaims?.roles ?? [];
        return roles.filter(role => !!role?.trim());
    }

    /**
     * `true` solo para una cuenta autenticada y sin un solo rol.
     *
     * Sin cuenta devuelve `false` a propósito: eso no es "sin permisos" sino "sin sesión", y de eso
     * se encarga `MsalGuard`. Expulsar acá taparía el login con la pantalla de bloqueo.
     */
    hasNoRoles(): boolean {
        return this.isAuthenticated && this.tokenRoles().length === 0;
    }

    /**
     * Manda la sesión a la pantalla de bloqueo.
     *
     * Idempotente dentro de la misma navegación: una pantalla dispara varias peticiones a la vez y
     * todas fallan con el mismo 403, así que el interceptor llama a esto N veces.
     */
    expel(): void {
        if (this.expelling || this.router.url.startsWith(NO_ACCESS_ROUTE)) {
            return;
        }

        this.expelling = true;
        this.clearRolesCache();
        void this.router.navigate([NO_ACCESS_ROUTE]).finally(() => (this.expelling = false));
    }

    /**
     * Cierra la sesión y vuelve a la app, para que Entra pida elegir cuenta.
     *
     * Es un logout completo y no un `loginRedirect({ prompt: 'select_account' })` por dos razones:
     * el prompt deja la sesión de Entra viva y la cuenta vieja en el cache de MSAL (quedan dos, y
     * `UserService` lee `getAllAccounts()[0]` — puede quedarse con la que ya no sirve); y sin cerrar
     * la sesión del IdP, volver a entrar puede resolverse por SSO silencioso con la MISMA cuenta.
     *
     * Al volver a `/` el `MsalGuard` dispara el login y, ya sin sesión en Entra, aparece el selector
     * de cuentas. Si la persona vuelve a elegir la cuenta sin roles, el guard la trae de nuevo a la
     * pantalla de bloqueo: no hay bucle, porque el logout nunca es automático.
     */
    signOutToSwitchAccount(): void {
        this.clearRolesCache();
        this.msal
            .logoutRedirect({
                account: this.account ?? undefined,
                postLogoutRedirectUri: '/'
            })
            .subscribe({ error: err => console.error('Error al cerrar sesión para cambiar de cuenta:', err) });
    }

    /**
     * Cierra la sesión y deja al usuario en la pantalla de sign-out de Microsoft.
     *
     * `postLogoutRedirectUri: null` es la forma que tiene MSAL de decir "no vuelvas a la app": sin
     * eso se cae en `environment.msalConfig.auth.postLogoutRedirectUri` (`/tender/home`), que está
     * detrás de `MsalGuard` y vuelve a lanzar el login de inmediato — el usuario nunca queda fuera.
     */
    signOut(): void {
        this.clearRolesCache();
        this.msal
            .logoutRedirect({
                account: this.account ?? undefined,
                postLogoutRedirectUri: null
            })
            .subscribe({ error: err => console.error('Error al cerrar sesión:', err) });
    }

    /** Login explícito, para la pantalla de bloqueo abierta sin sesión. */
    login(): void {
        this.msal
            .loginRedirect({ scopes: [...environment.apiConfig.scopes] } as RedirectRequest)
            .subscribe({ error: err => console.error('Error al iniciar sesión:', err) });
    }

    /**
     * Vacía el cache de roles resuelto por Graph antes de cambiar de cuenta.
     *
     * Vive 30 minutos y es un singleton de root: sin esto la cuenta nueva heredaría los roles de la
     * anterior en la misma pestaña. Se resuelve por `Injector` (no por constructor) para no arrastrar
     * `GraphService`/`HttpClient` a la cadena de DI del interceptor.
     */
    private clearRolesCache(): void {
        try {
            this.injector.get(UserRolesCacheService).clearCache();
        } catch {
            // Best-effort: el logout debe ocurrir igual si el cache no se pudo limpiar.
        }
    }
}
